import { useState, useEffect } from 'react';
import type { OrderableMenuDelays } from '@/hooks/useOrderableMenu';

export interface AsapQuote {
  startTime: Date;
  endTime: Date;
  formattedRange: string;
  label: string;
}

function roundToNearest5Minutes(date: Date): Date {
  const ms = 1000 * 60 * 5;
  return new Date(Math.round(date.getTime() / ms) * ms);
}

function formatTime(date: Date, timezone?: string | null): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  
  if (timezone) {
    options.timeZone = timezone;
  }
  
  return date.toLocaleTimeString('en-US', options).toLowerCase();
}

export function calculateAsapQuote(
  delays: OrderableMenuDelays | undefined | null,
  serviceType: 'takeout' | 'delivery',
  timezone?: string | null,
  now: Date = new Date()
): AsapQuote | null {
  if (!delays) return null;

  if (serviceType === 'takeout') {
    const pickupTime = new Date(now.getTime() + delays.delay * 1000);
    const startTime = roundToNearest5Minutes(pickupTime);
    const endTime = new Date(startTime.getTime() + 5 * 60 * 1000);

    return {
      startTime,
      endTime,
      formattedRange: `${formatTime(startTime, timezone)} - ${formatTime(endTime, timezone)}`,
      label: 'Ready',
    };
  } else {
    const dropoffTime = new Date(now.getTime() + delays.route * 1000);
    const startTime = roundToNearest5Minutes(dropoffTime);
    const endTime = new Date(startTime.getTime() + 15 * 60 * 1000);

    return {
      startTime,
      endTime,
      formattedRange: `${formatTime(startTime, timezone)} - ${formatTime(endTime, timezone)}`,
      label: 'Arrives',
    };
  }
}

export function useAsapQuoteRefresh(
  delays: OrderableMenuDelays | undefined | null,
  serviceType: 'takeout' | 'delivery',
  timezone?: string | null,
  refreshIntervalMs: number = 60000
) {
  const [quote, setQuote] = useState<AsapQuote | null>(() =>
    calculateAsapQuote(delays, serviceType, timezone)
  );

  useEffect(() => {
    setQuote(calculateAsapQuote(delays, serviceType, timezone));

    if (!delays) return;

    const interval = setInterval(() => {
      setQuote(calculateAsapQuote(delays, serviceType, timezone));
    }, refreshIntervalMs);

    return () => clearInterval(interval);
  }, [delays, serviceType, timezone, refreshIntervalMs]);

  return quote;
}
